import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Combobox,
  Option,
  Avatar,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

interface TenderingRequestUserGroup {
  userGroupID: number;
  userGroupName: string;
}

interface TenderingRequestGroupAdmin {
  userID: number;
  email: string;
  firstName: string;
  lastName: string;
}

interface TenderingRequestDialogProps {
  isOpen: boolean;
  mode?: 'join' | 'leave';
  tenderingName?: string;
  userGroups: TenderingRequestUserGroup[];
  selectedUserGroupIDs: string[];
  onSelectUserGroups: (userGroupIDs: string[]) => void;
  groupAdminsByUserGroupID: Record<string, TenderingRequestGroupAdmin[]>;
  loadingGroupAdminIDs: string[];
  onClose: () => void;
  onConfirm: () => void;
}

const TenderingRequestDialog: React.FC<TenderingRequestDialogProps> = ({
  isOpen,
  mode = 'join',
  tenderingName,
  userGroups,
  selectedUserGroupIDs,
  onSelectUserGroups,
  groupAdminsByUserGroupID,
  loadingGroupAdminIDs,
  onClose,
  onConfirm,
}) => {
  const hasUserGroups = userGroups.length > 0;
  const isLeave = mode === 'leave';
  const selectedUserGroupNames = userGroups
    .filter((ug) => selectedUserGroupIDs.includes(ug.userGroupID.toString()))
    .map((ug) => ug.userGroupName);

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface style={hasUserGroups ? { width: "600px", maxWidth: "600px", height: "600px" } : undefined}>
        <DialogBody style={hasUserGroups ? { height: "100%" } : undefined}>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={onClose}
              />
            }
          >
            {isLeave ? "Begär att lämna grupp" : "Begär tillgång till ramavtal"}
          </DialogTitle>
          <DialogContent className="flex flex-col gap-2" style={hasUserGroups ? { flexGrow: 1, overflowY: "auto" } : undefined}>
            {hasUserGroups ? (
              <p className="text-sm">
                {isLeave
                  ? `Vill du skicka en begäran om att lämna en grupp i ramavtalet "${tenderingName}"?`
                  : `Vill du skicka en begäran om att få tillgång i ramavtalet "${tenderingName}"?`}
              </p>
            ) : (
              <p className="text-sm">
                {isLeave
                  ? `Du tillhör inga grupper att lämna i ramavtalet "${tenderingName}".`
                  : `Det finns inga grupper att ansöka om i ramavtalet "${tenderingName}". Kontakta ramavtalsansvarig för att bli tillagd.`}
              </p>
            )}
            {hasUserGroups && (
              <Field label="Grupper:">
                <Combobox
                  multiselect
                  placeholder="Välj grupper"
                  value={selectedUserGroupNames.join(', ')}
                  selectedOptions={selectedUserGroupIDs}
                  onOptionSelect={(_event, data) => {
                    onSelectUserGroups(data.selectedOptions);
                  }}
                  // DialogContent scrolls internally, which otherwise clips the popup's available height to the dialog's bounds
                  positioning={{ autoSize: false, overflowBoundary: 'window' }}
                  listbox={{
                    style: {
                      maxHeight: '250px',
                      overflowY: 'auto',
                    },
                  }}
                >
                  {userGroups.map((group) => (
                    <Option key={group.userGroupID} value={group.userGroupID.toString()} text={group.userGroupName}>
                      {group.userGroupName}
                    </Option>
                  ))}
                </Combobox>
              </Field>
            )}
            {hasUserGroups && selectedUserGroupIDs.length > 0 && (
              <div className="flex flex-col gap-3">
                {selectedUserGroupIDs.map((userGroupID) => {
                  const group = userGroups.find((ug) => ug.userGroupID.toString() === userGroupID);
                  const admins = groupAdminsByUserGroupID[userGroupID] ?? [];
                  const isLoading = loadingGroupAdminIDs.includes(userGroupID);
                  return (
                    <Field key={userGroupID} label={`Behörighetsansvariga för ${group?.userGroupName ?? "gruppen"}:`}>
                      {isLoading ? (
                        <Spinner size="tiny" label="Hämtar behörighetsansvariga..." />
                      ) : admins.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {admins.map((admin) => (
                            <div key={admin.userID} className="flex items-center gap-2">
                              <Avatar name={`${admin.firstName} ${admin.lastName}`} />
                              <div className="flex flex-col">
                                <Text weight="semibold">{`${admin.firstName} ${admin.lastName}`}</Text>
                                <Text size={200}>{admin.email}</Text>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Text italic>Ingen behörighetsansvarig hittades för den valda gruppen.</Text>
                      )}
                    </Field>
                  );
                })}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {hasUserGroups ? "Avbryt" : "Stäng"}
            </Button>
            {hasUserGroups && (
              <Button
                appearance="primary"
                onClick={onConfirm}
                disabled={selectedUserGroupIDs.length === 0}
              >
                Bekräfta
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default TenderingRequestDialog;
